import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  formatContactName,
  getGradeLabel,
  getPermissionLabel,
  getPickupBadge,
  getStaffName,
  getStaffTitle,
  getStudentName,
  parseChildrenIds,
} from '@car-pool/shared/schoolRecords';
import { apiFetch, thumbUrls, uploadUrl } from '../config/api';
import { isSchoolAdminUser, isSuperAdminUser } from '../lib/adminAccess';
import { sendSupportRequest } from '../lib/support';
import { formatVisitWhen, visitReasonLabel, visitorTypeLabel } from '../lib/visitorForm';
import SchoolAvatar from './SchoolAvatar';
import SchoolSearchResultRow from './SchoolSearchResultRow';
import SchoolSupportModal from './SchoolSupportModal';

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', iconSrc: '/icons/dashboard.png', primary: true },
  { key: 'dismissal', label: 'Dismissal Console', section: 'Operations', iconSrc: '/icons/dismissal-console.png' },
  { key: 'field-trip-forms', label: 'Field Trip Forms', iconText: 'F' },
  { key: 'visitors', label: 'Visitors', iconText: 'V' },
  { key: 'students', label: 'Students', section: 'People', iconSrc: '/icons/students.png' },
  { key: 'parents', label: 'Parents', iconSrc: '/icons/parents.png' },
  { key: 'drivers', label: 'Drivers', iconText: 'D' },
  { key: 'staff', label: 'Staff', iconSrc: '/icons/staff.png' },
  { key: 'buses', label: 'Buses', iconText: 'B' },
  { key: 'messages', label: 'Messages', iconText: '@' },
  { key: 'push-notifications', label: 'Push Notifications', iconText: '!' },
  { key: 'pickup-zones', label: 'Pickup Zones', section: 'Carpool', iconSrc: '/icons/pickup-zones.png' },
  { key: 'gps-diagnostics', label: 'GPS Diagnostics', iconText: '◎' },
  { key: 'calendar', label: 'Calendar', section: 'Scheduling', iconSrc: '/icons/calendar.png' },
  { key: 'carpool-schedule', label: 'Carpool Schedule', iconText: '▦' },
  { key: 'settings', label: 'System Preferences', section: 'Settings', iconSrc: '/icons/system-preferences.png' },
];
const SEARCH_GROUP_LIMIT = 4;

function getSchoolMark(name = 'School') {
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((part) => !['academy', 'school'].includes(part.toLowerCase()));

  const source = parts.length ? parts : String(name).trim().split(/\s+/).filter(Boolean);
  if (!source.length) return 'SA';
  return source.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

function getSchoolLogoCandidates(logoPath) {
  const full = uploadUrl(logoPath);
  const thumbs = thumbUrls(logoPath);
  return [...thumbs, full].filter(Boolean);
}

function NavIcon({ item }) {
  if (item.iconSrc) {
    return <img className="sd-nav-icon-img" src={item.iconSrc} alt="" aria-hidden="true" />;
  }

  return <span className="sd-nav-icon-glyph">{item.iconText || '•'}</span>;
}

function getGradeLabelMap(gradeNames = []) {
  return Object.fromEntries(
    gradeNames
      .map((grade) => [Number(grade.grade_code), grade.label])
      .filter(([gradeCode]) => !Number.isNaN(gradeCode))
  );
}

function getUserDisplayName(user) {
  return user?.name || [user?.f_name, user?.l_name].filter(Boolean).join(' ').trim() || user?.email || 'Account';
}

function getUserRole(user) {
  if (user?.role) return user.role;
  if (user?.position_title) return user.position_title;
  if (user?.permission_level) return getPermissionLabel(user.permission_level);
  if (user?.user_type) {
    return String(user.user_type)
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return 'Super Admin';
}

function compareByName(left, right) {
  const leftKey = `${left?.l_name || ''} ${left?.f_name || ''}`.trim().toLowerCase();
  const rightKey = `${right?.l_name || ''} ${right?.f_name || ''}`.trim().toLowerCase();
  return leftKey.localeCompare(rightKey);
}

function matchesQuery(fields, query) {
  return fields.some((field) => String(field || '').toLowerCase().includes(query));
}

function getVisitorBadge(visit) {
  if (visit?.status === 'on_campus') return { label: 'On campus', tone: 'success', rank: 0 };
  if (visit?.checked_out_by_staff_id) return { label: 'Checked out', tone: 'muted', rank: 1 };
  return { label: 'Expired', tone: 'muted', rank: 2 };
}

function getStatusSortValue(status) {
  return String(status?.picked_at || status?.sent_at || status?.called_at || '');
}

function getLinkedStudents(parent, studentsById) {
  return parseChildrenIds(parent?.children_ids)
    .map((studentId) => studentsById[studentId])
    .filter(Boolean)
    .sort(compareByName);
}

function getParentResultMeta(parent, studentsById, dismissalByStudentId, gradeLabelMap, timeOptions = {}) {
  const linkedStudents = getLinkedStudents(parent, studentsById);
  const primaryStudent = linkedStudents[0] || null;

  const bestLinkedStudent = [...linkedStudents].sort((left, right) => {
    const leftBadge = getPickupBadge(left, dismissalByStudentId[left.id], timeOptions);
    const rightBadge = getPickupBadge(right, dismissalByStudentId[right.id], timeOptions);
    const rankDiff = leftBadge.rank - rightBadge.rank;

    if (rankDiff !== 0) return rankDiff;

    const timeDiff = getStatusSortValue(dismissalByStudentId[right.id]).localeCompare(getStatusSortValue(dismissalByStudentId[left.id]));
    if (timeDiff !== 0) return timeDiff;

    return compareByName(left, right);
  })[0] || null;

  let subtitle = parent?.phone || parent?.email || 'No linked students';
  if (primaryStudent) {
    const primaryLabel = `${getStudentName(primaryStudent)} (${getGradeLabel(primaryStudent.grade, gradeLabelMap, { short: true })})`;
    subtitle = linkedStudents.length > 1 ? `${primaryLabel} +${linkedStudents.length - 1} more` : primaryLabel;
  }

  return {
    subtitle,
    badge: bestLinkedStudent ? getPickupBadge(bestLinkedStudent, dismissalByStudentId[bestLinkedStudent.id], timeOptions) : null,
  };
}

export default function SchoolLayout({ token, navigate, schoolId, user, activePage, breadcrumb, headerRight, onLogout, children }) {
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [supportView, setSupportView] = useState(null);
  const [supportSuccessType, setSupportSuccessType] = useState(null);
  const [supportSending, setSupportSending] = useState(false);
  const [supportError, setSupportError] = useState(null);
  const [publicFeatureFlags, setPublicFeatureFlags] = useState({});
  const [supportDrafts, setSupportDrafts] = useState({
    report: '',
    question: '',
    feature: '',
  });
  const [schoolLogoIndex, setSchoolLogoIndex] = useState(0);
  const [searchIndex, setSearchIndex] = useState({
    students: [],
    contacts: [],
    staff: [],
    dismissalStatuses: [],
    gradeNames: [],
    visits: [],
  });
  const searchRootRef = useRef(null);
  const userMenuRef = useRef(null);
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const schoolLogoCandidates = useMemo(
    () => getSchoolLogoCandidates(school?.logo_relative_path),
    [school?.logo_relative_path]
  );
  const schoolLogoSrc = schoolLogoCandidates[schoolLogoIndex] || null;
  const schoolTimeOptions = useMemo(() => ({
    timeZone: school?.time_zone,
    isDst: Boolean(Number(school?.is_dst)),
  }), [school?.time_zone, school?.is_dst]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const schoolRes = await apiFetch(`/schools/${schoolId}`, {}, token);
      setSchool(schoolRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [schoolId, token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    apiFetch('/feature-flags')
      .then((response) => { if (active) setPublicFeatureFlags(response.data || {}); })
      .catch(() => { if (active) setPublicFeatureFlags({}); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setSchoolLogoIndex(0);
  }, [school?.logo_relative_path]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleSchoolLogoUpdated = (event) => {
      const detail = event.detail || {};
      if (String(detail.schoolId) !== String(schoolId)) return;

      setSchool((current) => (current ? {
        ...current,
        logo_relative_path: detail.logo_relative_path || null,
      } : current));
    };

    window.addEventListener('school-logo-updated', handleSchoolLogoUpdated);
    return () => window.removeEventListener('school-logo-updated', handleSchoolLogoUpdated);
  }, [schoolId]);

  useEffect(() => {
    let cancelled = false;

    setSearchLoading(true);
    setSearchQuery('');
    setSearchOpen(false);
    setProfileOpen(false);
    setSupportView(null);
    setSupportSuccessType(null);

    Promise.allSettled([
      apiFetch(`/students?school_id=${schoolId}`, {}, token),
      apiFetch(`/contacts?school_id=${schoolId}`, {}, token),
      apiFetch(`/staff?school_id=${schoolId}`, {}, token),
      apiFetch(`/dismissal-status?school_id=${schoolId}`, {}, token),
      apiFetch(`/school-grade-names?school_id=${schoolId}`, {}, token),
      apiFetch(`/visitors/visits?school_id=${schoolId}`, {}, token),
    ])
      .then(([studentsRes, contactsRes, staffRes, dismissalRes, gradeNamesRes, visitsRes]) => {
        if (cancelled) return;

        setSearchIndex({
          students: studentsRes.status === 'fulfilled' ? studentsRes.value.data || [] : [],
          contacts: contactsRes.status === 'fulfilled' ? contactsRes.value.data || [] : [],
          staff: staffRes.status === 'fulfilled' ? staffRes.value.data || [] : [],
          dismissalStatuses: dismissalRes.status === 'fulfilled' ? dismissalRes.value.data || [] : [],
          gradeNames: gradeNamesRes.status === 'fulfilled'
            ? (gradeNamesRes.value.data || []).filter((grade) => grade.is_active)
            : [],
          visits: visitsRes.status === 'fulfilled' ? visitsRes.value.data || [] : [],
        });
      })
      .finally(() => {
        if (!cancelled) {
          setSearchLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [schoolId, token]);

  useEffect(() => {
    if (!searchOpen && !profileOpen && !supportView) return undefined;

    const handlePointerDown = (event) => {
      if (supportView) return;

      if (!searchRootRef.current?.contains(event.target)) {
        setSearchOpen(false);
      }

      if (!userMenuRef.current?.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSearchOpen(false);
        setProfileOpen(false);
        setSupportView(null);
        setSupportSuccessType(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [profileOpen, searchOpen, supportView]);

  const gradeLabelMap = useMemo(
    () => getGradeLabelMap(searchIndex.gradeNames),
    [searchIndex.gradeNames]
  );

  const studentsById = useMemo(
    () => Object.fromEntries(searchIndex.students.map((student) => [student.id, student])),
    [searchIndex.students]
  );

  const dismissalByStudentId = useMemo(
    () => Object.fromEntries(searchIndex.dismissalStatuses.map((status) => [status.student_id, status])),
    [searchIndex.dismissalStatuses]
  );

  const parents = useMemo(
    () => searchIndex.contacts.filter((contact) => contact.type === 'parent').sort(compareByName),
    [searchIndex.contacts]
  );

  const normalizedQuery = deferredSearchQuery.trim().toLowerCase();
  const showSearchResults = searchOpen && Boolean(searchQuery.trim());

  const studentResults = useMemo(() => {
    if (!normalizedQuery) return [];

    return searchIndex.students
      .filter((student) => student.status !== 'inactive')
      .filter((student) => matchesQuery([
        student.f_name,
        student.l_name,
        student.student_email,
        student.student_phone,
        getGradeLabel(student.grade, gradeLabelMap),
      ], normalizedQuery))
      .sort((left, right) => {
        const leftBadge = getPickupBadge(left, dismissalByStudentId[left.id], schoolTimeOptions);
        const rightBadge = getPickupBadge(right, dismissalByStudentId[right.id], schoolTimeOptions);
        const rankDiff = leftBadge.rank - rightBadge.rank;

        if (rankDiff !== 0) return rankDiff;

        const timeDiff = getStatusSortValue(dismissalByStudentId[right.id]).localeCompare(getStatusSortValue(dismissalByStudentId[left.id]));
        if (timeDiff !== 0) return timeDiff;

        return compareByName(left, right);
      })
      .slice(0, SEARCH_GROUP_LIMIT);
  }, [dismissalByStudentId, gradeLabelMap, normalizedQuery, schoolTimeOptions, searchIndex.students]);

  const parentResults = useMemo(() => {
    if (!normalizedQuery) return [];

    return parents
      .filter((parent) => {
        const linkedStudents = getLinkedStudents(parent, studentsById);
        return matchesQuery([
          parent.f_name,
          parent.l_name,
          parent.email,
          parent.phone,
          ...linkedStudents.map((student) => getStudentName(student)),
          ...linkedStudents.map((student) => getGradeLabel(student.grade, gradeLabelMap)),
        ], normalizedQuery);
      })
      .slice(0, SEARCH_GROUP_LIMIT);
  }, [gradeLabelMap, normalizedQuery, parents, studentsById]);

  const staffResults = useMemo(() => {
    if (!normalizedQuery) return [];

    return [...searchIndex.staff]
      .filter((member) => matchesQuery([
        member.f_name,
        member.l_name,
        member.school_email,
        member.mobile_phone,
        member.position_title,
        getStaffTitle(member),
      ], normalizedQuery))
      .sort((left, right) => {
        if (Boolean(left.is_active) !== Boolean(right.is_active)) {
          return left.is_active ? -1 : 1;
        }

        return compareByName(left, right);
      })
      .slice(0, SEARCH_GROUP_LIMIT);
  }, [normalizedQuery, searchIndex.staff]);

  const visitorResults = useMemo(() => {
    if (!normalizedQuery) return [];

    return [...searchIndex.visits]
      .filter((visit) => matchesQuery([
        visit.full_name,
        visit.company,
        visit.destination_label,
        visit.badge_number,
        visit.notes,
        visitorTypeLabel(visit.visitor_type),
        visitReasonLabel(visit.visit_reason),
      ], normalizedQuery))
      .sort((left, right) => {
        const rankDiff = getVisitorBadge(left).rank - getVisitorBadge(right).rank;
        if (rankDiff !== 0) return rankDiff;
        return String(right.checked_in_at || '').localeCompare(String(left.checked_in_at || ''));
      })
      .slice(0, SEARCH_GROUP_LIMIT);
  }, [normalizedQuery, searchIndex.visits]);

  const handleSearchSelection = (target, data) => {
    setSearchOpen(false);
    setProfileOpen(false);
    setSupportView(null);
    setSupportSuccessType(null);
    setSearchQuery('');
    navigate(target, data);
  };

  const handleSidebarNav = (item) => {
    setProfileOpen(false);
    setSearchOpen(false);
    setSupportView(null);
    setSupportSuccessType(null);

    switch (item) {
      case 'dashboard': navigate('school-dashboard', schoolId); break;
      case 'dismissal': navigate('dismissal-console', schoolId); break;
      case 'field-trip-forms': navigate('field-trip-forms', schoolId); break;
      case 'visitors': navigate('visitors', schoolId); break;
      case 'students': navigate('students', schoolId); break;
      case 'parents': navigate('parents', schoolId); break;
      case 'drivers': navigate('drivers', schoolId); break;
      case 'staff': navigate('staff', schoolId); break;
      case 'buses': navigate('buses', schoolId); break;
      case 'messages': navigate('messages', schoolId); break;
      case 'push-notifications': navigate('push-notifications', schoolId); break;
      case 'pickup-zones': navigate('pickup-zones', schoolId); break;
      case 'gps-diagnostics': navigate('gps-diagnostics', schoolId); break;
      case 'calendar': navigate('calendar', schoolId); break;
      case 'carpool-schedule': navigate('carpool-schedule', schoolId); break;
      case 'settings': navigate('settings', schoolId); break;
      default: break;
    }
  };

  if (loading) return <div className="sa-empty">Loading...</div>;
  if (!school) return <div className="sa-error">School not found</div>;

  const userDisplayName = getUserDisplayName(user);
  const userRole = getUserRole(user);
  const userEmail = user?.email || user?.school_email || 'No email address';
  const supportActive = Boolean(supportView);
  const schoolScopedUser = isSchoolAdminUser(user);

  const openSupport = () => {
    setSearchOpen(false);
    setProfileOpen(false);
    setSupportSuccessType(null);
    setSupportView('menu');
  };

  const closeSupport = () => {
    setSupportView(null);
    setSupportSuccessType(null);
    setSupportError(null);
  };

  const handleSupportDraftChange = (view, value) => {
    setSupportDrafts((current) => ({
      ...current,
      [view]: value,
    }));
    if (supportError) setSupportError(null);
  };

  const handleSupportSend = async (view) => {
    const message = supportDrafts[view]?.trim();
    if (!message) return;
    if (supportSending) return;

    setSupportError(null);
    setSupportSending(true);

    try {
      await sendSupportRequest({
        supportType: view,
        message,
        user,
        school,
        schoolId,
        userDisplayName,
        userEmail,
        userRole,
      });

      setSupportDrafts((current) => ({
        ...current,
        [view]: '',
      }));
      setSupportSuccessType(view);
      setSupportView('success');
    } catch (err) {
      console.error('Failed to send support message:', err);
      setSupportError("We couldn't send your message. Please try again.");
    } finally {
      setSupportSending(false);
    }
  };

  const handleReturnToDashboard = () => {
    setSupportView(null);
    setSupportSuccessType(null);
    navigate('school-dashboard', schoolId);
  };

  return (
    <div className={`sd-layout ${showSearchResults ? 'search-active' : ''} ${supportActive ? 'support-active' : ''}`}>
      <header className="sd-topbar">
        <button
          className="sd-school-brand"
          onClick={() => {
            setProfileOpen(false);
            setSearchOpen(false);
            setSupportView(null);
            setSupportSuccessType(null);
            navigate('school-dashboard', schoolId);
          }}
        >
          <div className={`sd-school-logo ${schoolLogoSrc ? 'has-image' : ''}`} aria-hidden="true">
            {schoolLogoSrc ? (
              <img
                className="sd-school-logo-img"
                src={schoolLogoSrc}
                alt=""
                onError={() => setSchoolLogoIndex((current) => current + 1)}
              />
            ) : (
              <span className="sd-school-logo-mark">{getSchoolMark(school.legal_name)}</span>
            )}
          </div>
          <span className="sd-school-brand-name">{school.legal_name}</span>
        </button>

        <div className="sd-topbar-center">
          <div className="sd-search-wrap" ref={searchRootRef}>
            <label className={`sd-search ${showSearchResults ? 'active' : ''}`}>
              <span className="sd-search-icon" aria-hidden="true">⌕</span>
              <input
                type="text"
                value={searchQuery}
                onFocus={() => {
                  setProfileOpen(false);
                  setSupportView(null);
                  setSupportSuccessType(null);
                  setSearchOpen(true);
                }}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setProfileOpen(false);
                  setSupportView(null);
                  setSupportSuccessType(null);
                  setSearchOpen(true);
                }}
                placeholder="Type here to search..."
              />
            </label>

            {showSearchResults ? (
              <div className="sd-search-results">
                {searchLoading ? (
                  <div className="sd-search-loading">Searching school records...</div>
                ) : (
                  <>
                    <section className="sd-search-group">
                      <h3 className="sd-search-group-title">Students</h3>
                      {studentResults.length ? (
                        <div className="sd-search-group-list">
                          {studentResults.map((student) => (
                            <SchoolSearchResultRow
                              key={`student-${student.id}`}
                              title={`${getStudentName(student)} (${getGradeLabel(student.grade, gradeLabelMap, { short: true })})`}
                              badge={getPickupBadge(student, dismissalByStudentId[student.id], schoolTimeOptions)}
                              seed={student.id}
                              onClick={() => handleSearchSelection('students', { schoolId, selectStudentId: student.id })}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="sd-search-group-empty">No results</div>
                      )}
                    </section>

                    <section className="sd-search-group">
                      <h3 className="sd-search-group-title">Parents</h3>
                      {parentResults.length ? (
                        <div className="sd-search-group-list">
                          {parentResults.map((parent) => {
                            const resultMeta = getParentResultMeta(parent, studentsById, dismissalByStudentId, gradeLabelMap, schoolTimeOptions);
                            return (
                              <SchoolSearchResultRow
                                key={`parent-${parent.id}`}
                                title={formatContactName(parent)}
                                subtitle={resultMeta.subtitle}
                                badge={resultMeta.badge}
                                seed={parent.id}
                                onClick={() => handleSearchSelection('parents', { schoolId, selectParentId: parent.id })}
                              />
                            );
                          })}
                        </div>
                      ) : (
                        <div className="sd-search-group-empty">No results</div>
                      )}
                    </section>

                    <section className="sd-search-group">
                      <h3 className="sd-search-group-title">Staff</h3>
                      {staffResults.length ? (
                        <div className="sd-search-group-list">
                          {staffResults.map((member) => (
                            <SchoolSearchResultRow
                              key={`staff-${member.id}`}
                              title={getStaffName(member)}
                              subtitle={getStaffTitle(member)}
                              badge={member.is_active ? null : { label: 'Inactive account', tone: 'muted' }}
                              seed={member.id}
                              onClick={() => handleSearchSelection('staff', { schoolId, selectStaffId: member.id })}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="sd-search-group-empty">No results</div>
                      )}
                    </section>

                    <section className="sd-search-group">
                      <h3 className="sd-search-group-title">Visitors</h3>
                      {visitorResults.length ? (
                        <div className="sd-search-group-list">
                          {visitorResults.map((visit) => (
                            <SchoolSearchResultRow
                              key={`visit-${visit.id}`}
                              title={visit.full_name}
                              subtitle={`${visitorTypeLabel(visit.visitor_type)} · ${visit.destination_label} · ${formatVisitWhen(visit.checked_in_at)}`}
                              badge={getVisitorBadge(visit)}
                              seed={visit.visitor_id || visit.id}
                              onClick={() => handleSearchSelection('visitors', { schoolId, selectVisitId: visit.id })}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="sd-search-group-empty">No results</div>
                      )}
                    </section>
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
        <div className="sd-topbar-right" ref={userMenuRef}>
          {headerRight}
          <button
            type="button"
            className={`sd-user-trigger ${profileOpen ? 'active' : ''}`}
            onClick={() => {
              setSearchOpen(false);
              setSupportView(null);
              setSupportSuccessType(null);
              setProfileOpen((current) => !current);
            }}
          >
            <SchoolAvatar record={user} name={userDisplayName} className="sd-user-avatar" />
            <span className="sd-user-name">{userDisplayName}</span>
          </button>

          {profileOpen ? (
            <div className="sd-user-popover">
              <div className="sd-user-popover-header">
                <div className="sd-user-popover-copy">
                  <div className="sd-user-popover-name">{userDisplayName}</div>
                  <div className="sd-user-popover-role">{userRole}</div>
                  <div className="sd-user-popover-email">{userEmail}</div>
                </div>
                <SchoolAvatar record={user} name={userDisplayName} className="sd-user-avatar sd-user-avatar-large" />
              </div>

              <button
                type="button"
                className="sd-user-logout"
                onClick={() => {
                  setProfileOpen(false);
                  onLogout?.();
                }}
              >
                Logout
              </button>
            </div>
          ) : null}
        </div>
      </header>

      <div className="sd-shell">
        <aside className="sd-sidebar">
          <nav className="sd-nav">
            {NAV_ITEMS
              .filter((item) => item.key !== 'gps-diagnostics' || isSuperAdminUser(user))
              .map((item) => (
              <div key={item.key}>
                {item.section && (
                  <div className="sd-nav-section">
                    {item.section}
                  </div>
                )}
                <button
                  className={`sd-nav-item ${item.primary ? 'sd-nav-primary' : ''} ${activePage === item.key ? 'active' : ''}`}
                  onClick={() => handleSidebarNav(item.key)}
                >
                  <span className="sd-nav-icon">
                    <NavIcon item={item} />
                  </span>
                  <span className="sd-nav-label">{item.label}</span>
                </button>
              </div>
            ))}
          </nav>

          <div
            className="sd-help-card"
            style={{ '--sd-help-bg': `url(${process.env.PUBLIC_URL}/support_bg.png)` }}
          >
            <div className="sd-help-icon" aria-hidden="true">
              <span className="sd-help-icon-mark">?</span>
            </div>
            <strong>Need help?</strong>
            <p>Please reach out to the car-pool support team</p>
            <button type="button" className="sd-help-btn" onClick={openSupport}>Contact Support</button>
          </div>
        </aside>

        <div className="sd-main-area">
          <div className="sd-breadcrumb">
            <button
              onClick={() => navigate(schoolScopedUser ? 'school-dashboard' : 'dashboard', schoolScopedUser ? schoolId : null)}
            >
              {schoolScopedUser ? (school.school_nickname || school.legal_name || 'School') : 'Pages'}
            </button> / <span>{breadcrumb}</span>
          </div>

          <main className="sd-page-content">
            {children}
          </main>
        </div>
      </div>

      <SchoolSupportModal
        view={supportView}
        successType={supportSuccessType}
        drafts={supportDrafts}
        sending={supportSending}
        error={supportError}
        onClose={closeSupport}
        onBack={() => {
          setSupportSuccessType(null);
          setSupportError(null);
          setSupportView('menu');
        }}
        onSelectView={(view) => {
          setSupportSuccessType(null);
          setSupportError(null);
          setSupportView(view);
        }}
        onDraftChange={handleSupportDraftChange}
        onSend={handleSupportSend}
        onReturnToDashboard={handleReturnToDashboard}
        token={token}
        schoolId={schoolId}
        aiEnabled={Boolean(publicFeatureFlags.webapp_support)}
      />
    </div>
  );
}
